/**
 * QUIPWA — note conflicts awaiting the user's attention
 *
 * Replicated state (_state.db) never conflicts: its merge is conflict-free.
 * Note *text* can: if this device and another edited the same paragraph of a
 * note, the three-way text merge cannot decide. Then nothing is lost — the
 * other device's version stays in the note and this device's version is saved
 * as a copy next to it — and the user is told here.
 *
 * Persisted in localStorage so a reload cannot swallow the notice.
 */
import { create } from 'zustand'

export interface NoteConflict {
  id: string
  /** The note that kept the other device's text. */
  noteId: string
  title: string
  /** The copy that holds this device's text. */
  copyNoteId: string
  detectedAt: number
}

interface NoteConflictsState {
  conflicts: NoteConflict[]
  add: (conflict: NoteConflict) => void
  dismiss: (id: string) => void
  clear: () => void
}

const KEY = 'quipwa:note-conflicts'
/** Conflicts of the previous sync implementation; their shape no longer applies. */
const LEGACY_KEY = 'quipwa:pendingConflicts'

function load(): NoteConflict[] {
  try {
    localStorage.removeItem(LEGACY_KEY)
    const raw = localStorage.getItem(KEY)
    return raw ? (JSON.parse(raw) as NoteConflict[]) : []
  } catch {
    return []
  }
}

function save(conflicts: NoteConflict[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(conflicts))
  } catch {
    /* storage full or disabled: the in-memory list still shows */
  }
}

export const useNoteConflicts = create<NoteConflictsState>((set, get) => ({
  conflicts: load(),
  add: (conflict) => {
    const conflicts = [...get().conflicts.filter((c) => c.noteId !== conflict.noteId), conflict]
    save(conflicts)
    set({ conflicts })
  },
  dismiss: (id) => {
    const conflicts = get().conflicts.filter((c) => c.id !== id)
    save(conflicts)
    set({ conflicts })
  },
  clear: () => {
    save([])
    set({ conflicts: [] })
  },
}))
